import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { lazy, Suspense } from "react";
import logo from "@/assets/mind-media-logo.svg";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import { Footer } from "@/components/ui/footer";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { allPostsQueryOptions, sidebarWidgetsQueryOptions } from "@/lib/blog";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { PostCard, FeaturedPostCard } from "@/components/blog/PostCard";

const ContactSection = lazy(() =>
  import("@/components/sections/ContactSection").then((m) => ({ default: m.ContactSection })),
);

const SIDEBAR_ID = "blog-sidebar";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog — Design & marketing insights | Mind Media" },
      {
        name: "description",
        content:
          "Practical web design, SEO, branding, and conversion insights from Mind Media. Case studies, tips, and resources for growing your business online.",
      },
      { property: "og:title", content: "Mind Media Blog — Design & marketing insights" },
      {
        property: "og:description",
        content: "Practical tips, case studies, and resources to help your business grow online.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mindmedia.co/blog" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/blog" }],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(allPostsQueryOptions),
      context.queryClient.ensureQueryData(sidebarWidgetsQueryOptions(SIDEBAR_ID)),
    ]);
  },
  component: BlogIndex,
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
});

function BlogIndex() {
  const { data } = useSuspenseQuery(allPostsQueryOptions);
  const { data: widgets } = useSuspenseQuery(sidebarWidgetsQueryOptions(SIDEBAR_ID));

  const [active, setActive] = useState<string>("all");
  const [query, setQuery] = useState<string>("");
  const [visibleCount, setVisibleCount] = useState<number>(6);

  const filtered = useMemo(() => {
    let list = data.posts;
    if (active !== "all") list = list.filter((p) => p.categorySlugs.includes(active));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q),
      );
    }
    return list;
  }, [data.posts, active, query]);

  const featured = useMemo(() => data.posts[0] ?? null, [data.posts]);

  const nonFeatured = useMemo(
    () =>
      featured && active === "all" && !query.trim() && filtered.length > 1
        ? filtered.filter((p) => p.slug !== featured.slug)
        : filtered,
    [active, featured, filtered, query],
  );

  const visiblePosts = useMemo(
    () => nonFeatured.slice(0, visibleCount),
    [nonFeatured, visibleCount],
  );
  const hasMore = visiblePosts.length < nonFeatured.length;

  useEffect(() => {
    setVisibleCount(6);
  }, [active, query]);

  return (
    <main className="min-h-screen bg-background">
      <div className="relative">
        <div className="absolute inset-0 overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -left-40 -top-20 z-0 h-[900px] w-[900px] rounded-full bg-hero-glow-warm opacity-20 blur-[120px] sm:-left-56 sm:-top-32 sm:h-[1200px] sm:w-[1200px] sm:blur-[160px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-40 -top-20 z-0 h-[900px] w-[900px] rounded-full bg-hero-glow-cool opacity-25 blur-[120px] sm:-right-56 sm:-top-32 sm:h-[1200px] sm:w-[1200px] sm:blur-[160px]"
          />
        </div>
        <section className="relative z-20 pb-4 text-center">
          <HeroNav
            logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
            navigation={[
              { label: "Work", href: "/#work" },
              { label: "About", href: "/#about" },
              { label: "Services", href: "/services" },
              { label: "Pricing", href: "/#pricing" },
              { label: "FAQ", href: "/#faq" },
              { label: "Contact", href: "#contact" },
            ]}
            ctaButton={{ label: "Book a Meeting", href: "/#contact" }}
          />
        </section>

        {/* Filters */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-10">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="font-display text-3xl font-bold leading-none tracking-tight text-foreground sm:text-4xl">
              Blog
            </h1>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <FilterChip label="All" active={active === "all"} onClick={() => setActive("all")} />
              {data.categories.map((c) => (
                <FilterChip
                  key={c.slug}
                  label={c.name}
                  active={active === c.slug}
                  onClick={() => setActive(c.slug)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Featured */}
        {featured && active === "all" && !query ? (
          <section className="relative z-10 mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-10">
            <FeaturedPostCard post={featured} />
          </section>
        ) : null}

        {/* Latest + sidebar */}
        <section className="relative z-10 mx-auto max-w-7xl px-4 pt-10 pb-12 sm:px-6 sm:pt-14 sm:pb-16 lg:px-10">
          <div className="grid gap-8 lg:grid-cols-4">
            <div className="lg:col-span-3">
              <div className="mb-5">
                <h2 className="font-display text-2xl font-bold text-gray-950 sm:text-3xl">
                  Latest articles
                </h2>
              </div>
              {nonFeatured.length === 0 ? (
                <div className="rounded-3xl bg-white p-12 text-center text-sm text-gray-500 shadow-sm ring-1 ring-black/5">
                  No articles found.
                </div>
              ) : (
                <>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {visiblePosts.map((p) => (
                      <PostCard key={p.slug} post={p} />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setVisibleCount((c) => c + 6)}
                        className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-gray-700 ring-1 ring-black/10 transition hover:-translate-y-0.5 hover:shadow-sm"
                      >
                        Load more articles
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="lg:col-span-1">
              <BlogSidebar
                categories={data.categories}
                totalCount={data.posts.length}
                widgets={widgets}
                initialSearch={query}
                onSearch={setQuery}
                activeCategory={active}
                onCategoryClick={setActive}
              />
            </div>
          </div>
        </section>
      </div>

      <Suspense fallback={<div className="min-h-[80px]" />}>
        <ContactSection compact />
      </Suspense>

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/the-problem", label: "The Problem" },
          { href: "/#work", label: "Work" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "cursor-pointer rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
          : "cursor-pointer rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-black/10 transition hover:-translate-y-0.5 hover:shadow-sm"
      }
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
